import { WidgetExternalApplicationType } from '../../../../types/widget';
import ExternalApplicationItem from '../components/ExternalApplicationItem';
import ExternalApplicationForm from '../configuration/ExternalApplicationForm';
import { ExternalApplicationDetails, ExternalApplicationType } from '../types/externalApplication';
import { FormFieldEnum, SpecificFieldsType } from '../types/form';

const regions = [
	{
		id: 'eu1',
		name: 'Europe',
	},
	{
		id: 'us1',
		name: 'USA',
	},
];

type RegionType = {
	id: string;
	name: string;
};

type YoozSpecificType = {
	region: string;
	kcIdpHint?: string;
};

type YoozFormProps = {
	application: WidgetExternalApplicationType;
	isNew: boolean;
	onSubmit: (application: WidgetExternalApplicationType) => void;
	onRemove: (application: WidgetExternalApplicationType) => void;
};

export const YoozForm = ({ application, isNew, onSubmit, onRemove }: YoozFormProps) => {
	const { key, specific } = application;
	const jsonSpecific = specific ? (JSON.parse(specific) as YoozSpecificType) : undefined;
	const { region: initialRegion, kcIdpHint: initialKcIdpHint } = jsonSpecific ?? {};

	const defaultKcIdpHint = isNew ? 'fidorg_rising_idp' : undefined;
	const defaultRegion = isNew ? regions.find(({ id }) => id === 'eu1') : undefined;

	const handleSubmit = (yoozApplication: WidgetExternalApplicationType) => {
		const { specific: yoozSpecific } = yoozApplication as Omit<WidgetExternalApplicationType, 'specific'> &
			Required<Pick<WidgetExternalApplicationType, 'specific'>>;
		const jsonYoozSpecific = JSON.parse(yoozSpecific) as {
			region: RegionType;
			kcIdpHint: YoozSpecificType['kcIdpHint'];
		};

		const { region, kcIdpHint } = jsonYoozSpecific;

		onSubmit({ ...yoozApplication, specific: JSON.stringify({ region: region.id, kcIdpHint }) });
	};

	const specificFields: SpecificFieldsType[] = [
		{
			key,
			name: 'region',
			type: FormFieldEnum.AUTOCOMPLETE,
			value: regions.find(({ id }) => id === initialRegion) || defaultRegion,
			description: "Région de l'application.",
			label: 'Région',
			dataSource: () =>
				Promise.resolve({
					items: regions,
					count: regions.length,
				}),
			getOptionLabel: (option) => (option as RegionType).name,
			disableClearable: true,
			rules: {
				required: true,
			},
		},
		{
			key,
			name: 'kcIdpHint',
			type: FormFieldEnum.TEXTFIELD,
			value: initialKcIdpHint || defaultKcIdpHint,
			description: 'Configuration SSO fournie par Yooz.',
			label: 'Indice SSO',
		},
	];

	return (
		<ExternalApplicationForm
			application={application}
			isNew={isNew}
			specificFields={specificFields}
			onSubmit={handleSubmit}
			onRemove={onRemove}
		/>
	);
};

const Yooz = ({ name }: ExternalApplicationType) => (
	<ExternalApplicationItem name={name} imageSrc="/widgets/images/yooz-logo.png" />
);

export const getDetails = ({
	name,
	specific,
}: Pick<ExternalApplicationType, 'name' | 'specific'>): ExternalApplicationDetails => {
	const jsonSpecific = specific ? (JSON.parse(specific) as YoozSpecificType) : undefined;
	const { region, kcIdpHint } = jsonSpecific ?? {};

	if (!region) {
		throw new Error('The Yooz app region is missing.');
	}

	const queryString = kcIdpHint ? `?kc_idp_hint=${kcIdpHint}` : '';

	return {
		name,
		url: `https://${region}.getyooz.com/${queryString}`,
		component: <Yooz name={name} specific={specific} />,
	};
};
